import { FC, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { reduceWithSum } from '../../utils/arrayUtils';
import { getTextColor } from '../../utils/colorUtils';
import { nameof } from '../../utils/stringUtils';
import useReadableColor from '../../shared/hooks/useReadableColor';

//#region [1] Bar
const BarContainer = styled.div<{ showBarBackground: boolean; fontSize?: number }>`
  width: 100%;
  min-height: 19px; // for when there is no text
  font-size: ${p => p.fontSize ?? 14}px;
  border-radius: 0.25rem;
  background-color: ${p => p.showBarBackground ? `${p.theme.palette.neutralQuaternaryAlt}80` : undefined};

  position: relative;
  //overflow: hidden;
`;

const BarFill = styled.div<{ widthPercent: number; backgroundColor: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  
  width: max(1px, ${p => p.widthPercent}%);
  border-radius: ${p => p.widthPercent === 100 ? '0.25rem' : '0.25rem 0.125rem 0.125rem 0.25rem'};
  background-color: ${p => p.backgroundColor};

  overflow: hidden;
`;

const BarTextContainer = styled.div<{ textColor?: string; width?: number }>`
  width: ${p => p.width !== undefined ? `${p.width}px` : '0%'};
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 0 0.25rem;
  color: ${p => p.textColor};
  font-weight: 500;

  &, > * {
    white-space: nowrap;
  }
`;

const BarText: FC<{ text: string; valueText: string | number; textColor?: string; width?: number }> = ({ text, valueText, textColor, width }) => {
  const labelId = useId();
  return <BarTextContainer textColor={textColor} width={width}>
    <span id={labelId} title={text}>{text}</span>
    <span aria-labelledby={labelId}>{valueText}</span>
  </BarTextContainer>;
};

const TargetMarker = styled.div<{ percent: number }>`
  width: 2px;
  position: absolute;
  top: -3px;
  bottom: -3px;
  left: ${p => p.percent * 0.99}%;

  background-color: ${p => p.theme.palette.themePrimary};
`;

interface IBarProps {
  fontSize?: number;
  percent: number;
  targetPercent?: number;
  color: string;
  showBarBackground: boolean;
  value: number;
  text: string;
  valueFormatter(value: number): string;
}

const Bar: FC<IBarProps> = ({ fontSize, percent, targetPercent, color, showBarBackground, value, text, valueFormatter }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [textWidth, setTextWidth] = useState<number | undefined>(containerRef.current?.clientWidth);

  useEffect(
    () => {
      if (!containerRef.current)
        return;

      function updateTextWidth() {
        setTextWidth(containerRef.current?.clientWidth);
      }

      updateTextWidth();

      const observer = new ResizeObserver(updateTextWidth);
      observer.observe(containerRef.current);

      return () => observer.disconnect();
    },
    [],
  );

  return (
    <BarContainer ref={containerRef} showBarBackground={showBarBackground} fontSize={fontSize}>
      <BarText text={text} valueText={valueFormatter(value)} textColor={useReadableColor(color, undefined, showBarBackground ? 4 : 3.5)} width={textWidth} />
      {targetPercent !== undefined && <TargetMarker percent={targetPercent} />}
      <BarFill widthPercent={percent} backgroundColor={color}>
        <BarText text={text} valueText={valueFormatter(value)} textColor={getTextColor(color)} width={textWidth} />
      </BarFill>
    </BarContainer>
  );
};
//#endregion

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const defaultValueFormatter = (val: number) => val.toString();

export interface ChartValue {
  color: string;
  value: number;
  target?: number;
  text: string;
}

interface IProps {
  values: ChartValue[];
  maxValue?: number;
  valueFormatter?(value: number): string;
  showBarBackground?: boolean;
  fillAvailableSpace?: boolean;
  fontSize?: number;
}

export type ITheUltimateBarChartProps = IProps;

const TheUltimateBarChart: FC<IProps> = ({ values, maxValue, valueFormatter = defaultValueFormatter, showBarBackground, fillAvailableSpace, fontSize }) => {

  const max = useMemo(
    () => Math.max(...values.map((val) => Math.max(val.value, val.target ?? 0))) || 1,
    [values],
  );

  const sum = useMemo(
    () => Math.max(reduceWithSum(values, (val) => val.value), max) || 1, // max included to correctly calculate with targets
    [max, values],
  );

  const reference = maxValue ?? (fillAvailableSpace ? max : sum);

  return (
    <Container>
      {values.map((val, i) =>
        <Bar
          key={i}
          fontSize={fontSize}
          percent={Math.min(Math.round(val.value / reference * 100), 100)}
          targetPercent={val.target !== undefined ? Math.min(Math.round(val.target / reference * 100), 100) : undefined}
          showBarBackground={!!showBarBackground}
          valueFormatter={valueFormatter}
          {...val}
        />,
      )}
    </Container>
  );
};

TheUltimateBarChart.displayName = nameof({ TheUltimateBarChart });
export default TheUltimateBarChart;